// A tab opened before a deploy keeps the old front, which then talks to the
// new API: a single view property that disappeared is enough to crash a
// screen. Each build publishes its id in /version.json (plugin in
// shared/client/vite-config.ts); the tab re-reads it now and then and, once
// stale, the next navigation reloads the page instead of switching screens
// client-side. Never reload mid-task: an unsaved form would be lost.
import type { createBrowserRouter } from "react-router";

// "dev" under the Vite dev server: no /version.json, nothing to watch.
declare const __BUILD_ID__: string;

const CHECK_INTERVAL_MS = 5 * 60_000;
const MIN_GAP_MS = 60_000;

let stale = false;
let lastCheck = 0;
let pending: Promise<boolean> | null = null;

/** True when the server serves a different build than the one running in this tab. */
export function checkForNewBuild({ force = false } = {}): Promise<boolean> {
  if (__BUILD_ID__ === "dev" || stale) return Promise.resolve(stale);
  if (pending) return pending;
  if (!force && Date.now() - lastCheck < MIN_GAP_MS) return Promise.resolve(false);
  lastCheck = Date.now();
  pending = fetch("/version.json", { cache: "no-store" })
    .then((response) => (response.ok ? response.json() : null))
    .then((body: { build?: unknown } | null) => {
      if (typeof body?.build === "string" && body.build !== __BUILD_ID__) stale = true;
      return stale;
    })
    // Offline or unreadable response: conclude nothing.
    .catch(() => false)
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** Watches the served build and reloads on the first navigation after a deploy. */
export function watchBuildVersion(router: ReturnType<typeof createBrowserRouter>) {
  if (__BUILD_ID__ === "dev") return;
  let location = router.state.location;
  router.subscribe((state) => {
    const moved = state.location.pathname !== location.pathname || state.location.search !== location.search;
    location = state.location;
    if (!moved) return;
    if (stale) window.location.reload();
    else void checkForNewBuild();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkForNewBuild();
  });
  window.setInterval(() => {
    if (document.visibilityState === "visible") void checkForNewBuild();
  }, CHECK_INTERVAL_MS);
}
